import { Button, Label, Spinner, Textarea } from 'flowbite-react';
import React, { useEffect, useMemo } from 'react';
import { MdAdd } from 'react-icons/md';
import { RxCross1 } from 'react-icons/rx';
import { useMutation, useQueryClient } from 'react-query';
import { SingleValue } from 'react-select';
import Select from 'react-select';
import { Dropdown } from 'shared-utils';
import { assignBehaviouralFlag } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { BehvaiouralFlagDetail, BehvaiouralFlagDetailDocument } from 'types';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';

interface Props {
	userId: string;
	positionId: string;
	initialFlags?: BehvaiouralFlagDetailDocument[];
	isLoading?: boolean;
	onFinish?: () => void;
	onSubmitted?: (flags: BehvaiouralFlagDetailDocument[]) => void;
}

const AMAssignBehaviouralFlagToCandidate = ({
	userId,
	initialFlags,
	onFinish,
	isLoading,
	positionId,
	onSubmitted,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();

	// Fetch behavioural flags from dropdown menu
	const { data: behaviouralFlagsData, isLoading: isLoadingFlags } = useGetDropdownItemsByCategory('BEHAVIOURAL_FLAG', undefined, true);
	const behaviouralFlagOptions = useMemo(() => {
		if (!behaviouralFlagsData?.lmsTechStacks) return [];
		return behaviouralFlagsData.lmsTechStacks.map(flag => ({
			label: flag.label || flag.name,
			value: flag.name, // Use name as value (this is what gets stored in DB)
		}));
	}, [behaviouralFlagsData]);

	const [flags, setFlags] = React.useState<BehvaiouralFlagDetail[]>(
		initialFlags ?? [{ flag: '', comments: '' }],
	);

	const assignBehaviouralFlagFn = useMutation(['assignBehaviouralFlag'], assignBehaviouralFlag, {
		onSuccess() {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: 'Flags assigned successfully',
				type: 'success',
			});
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		if (initialFlags?.length) setFlags(initialFlags);
	}, [initialFlags]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				const cleanFlags = flags.map(({ flag, comments }) => ({ flag, comments }));
				if (onSubmitted) return onSubmitted(cleanFlags as BehvaiouralFlagDetailDocument[]);
				else {
					try {
						assignBehaviouralFlagFn.mutate({
							positionId,
							userId,
							flags: cleanFlags,
						});
					} catch (err) {
						console.log('err:', err);
						toast({
							title: `Something went wrong! ${err}`,
							type: 'error',
						});
					}
				}
			}}
		>
			<div className="mb-4 block whiteBgSelect min-h-[400px]">
				<div className="flex justify-between w-full pb-2">
					<div className="mb-[4px]">
						<Label>{'Select behavioural flags '}</Label>
						<span className="text-[red]">*</span>
					</div>
					<Button
						color="purplePlaced"
						disabled={flags.some(f => !f.flag || !f.comments)}
						onClick={() =>
							setFlags(prev => [
								...prev,
								{
									flag: '',
									comments: '',
								},
							])
						}
					>
						Add <MdAdd />
					</Button>
				</div>
				<div>
					{flags.map((flag, flagIndex) => (
						<div key={flagIndex} className="py-4 flex gap-2">
							<div className="w-2/5">
								<Label className="flex items-center">
									Flag <div className="text-red-500">*</div>
								</Label>
								<Select
									closeMenuOnSelect
									classNamePrefix="my-select-plans"
									options={behaviouralFlagOptions}
									onChange={(val: SingleValue<Dropdown>) => {
										if (val) {
											setFlags(prev =>
												prev.map((f, ind) =>
													flagIndex === ind ? { ...f, flag: val.value } : f,
												),
											);
										}
									}}
									value={behaviouralFlagOptions.find(opt => opt.value === flag.flag) || null}
									isLoading={isLoadingFlags}
									placeholder={isLoadingFlags ? 'Loading flags...' : 'Select flag'}
								/>
							</div>
							<div className="w-3/5">
								<Label className="flex items-center pt-[2px]">
									Comments <div className="text-red-500">*</div>
								</Label>
								<Textarea
									className="w-full"
									placeholder="Comments"
									value={flag.comments}
									onChange={e => {
										setFlags(prev =>
											prev.map((f, ind) => (flagIndex === ind ? { ...f, comments: e.target.value } : f)),
										);
									}}
								/>
							</div>
							<RxCross1 size={10} onClick={() => setFlags(prev => prev.filter((f, ind) => flagIndex !== ind))} />
						</div>
					))}
				</div>
			</div>

			<Button
				color="purplePlaced"
				type="submit"
				className="mt-[20px] w-full bg-[#7777E7]"
				disabled={assignBehaviouralFlagFn.isLoading || isLoading || !flags.every(flag => flag.comments)}
			>
				{assignBehaviouralFlagFn.isLoading || isLoading ? <Spinner /> : 'Assign Flags'}
			</Button>
		</form>
	);
};

export default AMAssignBehaviouralFlagToCandidate;
